import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, RefreshCw, Inbox, Search, Loader2, Mail, Phone, Building2, Wrench } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";

export default function Admin() {
  const { admin, logout, authAxios } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authAxios().get("/admin/submissions");
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
      } else {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, [authAxios, logout, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await authAxios().delete(`/admin/submissions/${toDelete.id}`);
      setItems((prev) => prev.filter((i) => i.id !== toDelete.id));
      toast.success("Submission deleted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setDeleting(false);
      setToDelete(null);
    }
  };

  const onLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const filtered = items.filter((i) => {
    const q = query.toLowerCase();
    return !q || [i.name, i.email, i.phone, i.company, i.service, i.message].some((v) => (v || "").toLowerCase().includes(q));
  });

  const fmtDate = (d) => new Date(d).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

  return (
    <div data-testid="admin-page" className="min-h-[calc(100vh-5rem)] bg-[#FAFAFA]">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#8B4513]">Admin</span>
            <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight mt-2">Contact Submissions</h1>
            <p className="text-[#594A42] mt-2 text-sm">Signed in as {admin?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh-btn" onClick={load} className="inline-flex items-center gap-2 border border-[#E5E1DE] bg-white text-[#1A110B] font-semibold text-sm px-4 py-2.5 rounded-sm hover:border-[#00B4C4] transition-colors">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button data-testid="admin-logout-btn" onClick={onLogout} className="inline-flex items-center gap-2 bg-[#1A110B] text-white font-semibold text-sm px-4 py-2.5 rounded-sm hover:bg-black transition-colors">
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B7A70]" />
            <Input data-testid="admin-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search submissions..." className="pl-9 bg-white focus-visible:ring-[#00B4C4]" />
          </div>
          <span data-testid="admin-count" className="text-sm text-[#594A42] font-semibold whitespace-nowrap">{filtered.length} {filtered.length === 1 ? "message" : "messages"}</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32 text-[#8B7A70]"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading...</div>
        ) : filtered.length === 0 ? (
          <div data-testid="admin-empty" className="flex flex-col items-center justify-center py-32 text-[#8B7A70] border border-dashed border-[#E5E1DE] rounded-sm bg-white">
            <Inbox className="h-10 w-10 mb-3" />
            <p className="font-semibold">No submissions {query ? "match your search" : "yet"}.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((s) => (
              <div key={s.id} data-testid={`submission-${s.id}`} className="bg-white border border-[#E5E1DE] rounded-sm p-6 hover:border-[#00B4C4]/50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-bold text-lg text-[#1A110B]">{s.name}</h3>
                      {s.sms_consent && (
                        <span data-testid={`sms-badge-${s.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#00B4C4]/10 text-[#00808c] border border-[#00B4C4]/30 px-2 py-0.5 rounded-sm">
                          SMS Opt-in
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-[#594A42]">
                      <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1.5 hover:text-[#00B4C4]"><Mail className="h-3.5 w-3.5" /> {s.email}</a>
                      {s.phone && <a href={`tel:${s.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#00B4C4]"><Phone className="h-3.5 w-3.5" /> {s.phone}</a>}
                      {s.company && <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> {s.company}</span>}
                      {s.service && <span className="inline-flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" /> {s.service}</span>}
                    </div>
                  </div>
                  <button data-testid={`delete-btn-${s.id}`} onClick={() => setToDelete(s)} className="shrink-0 inline-flex items-center gap-1.5 text-red-600 hover:text-white hover:bg-red-600 border border-red-200 text-xs font-semibold px-3 py-2 rounded-sm transition-colors">
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
                <p className="mt-4 text-[#1A110B] leading-relaxed whitespace-pre-wrap border-t border-[#F0EDEA] pt-4">{s.message}</p>
                <p className="mt-3 text-xs text-[#8B7A70]">{fmtDate(s.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this submission?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the message from {toDelete?.name}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-btn" onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
